/**
 * Board E's decisions on a conversation's exercises outside the catalogue, as one module over every
 * draft of the thread: the state of `model/exerciseDecisions.ts`, the acts that move it through its
 * named events, and the one timer the owner asked for — the draft opening in the builder by itself
 * after the last decision.
 *
 * Every rule is in `model/`: which rows stand resolved, what each says, what the batch adds and when
 * the draft opens (`outsideCatalogue.ts`), and how a decision moves the state (`exerciseDecisions.ts`).
 * Every act takes ONE path — sent, then landed or refused — whichever control made it, the creator's
 * save included. The page answers through `ExerciseDecisionHost`, which is where the catalogue
 * writes happen (a feature may not reach the exercise creator).
 */

import { useEffect, useEffectEvent, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import {
  decisionKey,
  type AiBuildResult,
  type AiConversation,
  type AiNamedDecision,
  type AiUnresolvedExercise,
} from '~/entities/ai-conversation';

import {
  addFailureOf,
  draftDecisionReading,
  exerciseDecisionReducer,
  initialDecisionState,
  type DecisionRoute,
} from '../model/exerciseDecisions';
import { AUTO_OPEN_DELAY_MS, autoOpenDue, outsideCatalogueView, type OutsideCatalogueView } from '../model/outsideCatalogue';

/** What a quick add reports: a pick of each exercise it added, and each name it could not add with the status it met. */
export interface QuickAddOutcome {
  readonly added: readonly AiNamedDecision[];
  /** `status` is the HTTP answer, `null` when there was none. */
  readonly failed: readonly { readonly name: string; readonly status: number | null }[];
}

/** Where the catalogue is written for a name: on this host, or on the Buddy page the host hands over to. */
export type ExerciseCatalogueHost =
  | {
      readonly kind: 'here';
      /**
       * Add each name's proposal to the catalogue as it stands, one after another. Picking them is the
       * list's next step, in one resolution for all of them.
       */
      readonly quickAdd: (turnId: number, exercises: readonly AiUnresolvedExercise[]) => Promise<QuickAddOutcome>;
      /**
       * Open the creator on the name, filled from its proposal; answers the saved exercise's id, or
       * `null` when the creator closed unsaved. Focus goes back to `opener` when it closes.
       */
      readonly verify: (turnId: number, exercise: AiUnresolvedExercise, opener: HTMLElement | null) => Promise<number | null>;
    }
  | {
      readonly kind: 'page';
      /** The Buddy page, with this name's row open: the builder's panel cannot write the catalogue. */
      readonly open: (turnId: number, name: string) => void;
    };

/** Where the page answers the list: the decisions, and the catalogue writes that end in one. */
export interface ExerciseDecisionHost {
  /** Picks and skips on names of draft `turnId`, sent in one write; answers whether it landed. */
  readonly decide: (turnId: number, decisions: readonly AiNamedDecision[]) => Promise<boolean>;
  readonly catalogue: ExerciseCatalogueHost;
}

/** The decisions of one draft, as its list and its program card read them. Every act answers whether its decision landed. */
export interface DraftDecisions {
  readonly view: OutsideCatalogueView;
  /** The draft is about to open by itself: the list's foot and its status say so. */
  readonly opening: boolean;
  /** A write about the name is on its way: its controls wait for it. */
  readonly busy: (name: string) => boolean;
  /** That write is a quick add, from the catalogue write to the pick that follows it landing. */
  readonly adding: (name: string) => boolean;
  readonly toggle: (name: string) => void;
  readonly reopen: (name: string) => void;
  /** „Zostaw obecny wybór": the reopened decision stands again, unchanged. */
  readonly keep: (name: string) => void;
  readonly pick: (name: string, exerciseId: number, target: string) => Promise<boolean>;
  readonly skip: (name: string) => Promise<boolean>;
  /** `null` where the catalogue cannot be written here: the row hands the name over instead (`handOver`). */
  readonly quickAdd: ((exercises: readonly AiUnresolvedExercise[]) => Promise<boolean>) | null;
  readonly verify: ((exercise: AiUnresolvedExercise, opener: HTMLElement | null) => Promise<boolean>) | null;
  /** „Zdecyduj na stronie Buddy’ego": the builder's panel, where the catalogue is not written. */
  readonly handOver: ((name: string) => void) | null;
  /** „Otwórz w builderze": the trainer's own press, which the timer then has nothing left to do. */
  readonly open: () => void;
}

interface DecisionsOptions {
  readonly conversation: AiConversation;
  readonly host: ExerciseDecisionHost;
  /** The decisions on their way to the server, under their `decisionKey`. */
  readonly deciding: ReadonlySet<string>;
  readonly onOpenDraft: (turnId: number, result: AiBuildResult) => void;
  /** The draft opens by itself after the last decision: the Buddy page, never the builder's panel. */
  readonly autoOpen: boolean;
  /** The row the address asked to open, handed over from the builder's panel. */
  readonly initialRow: { readonly turnId: number; readonly name: string } | null;
}

export function useExerciseDecisions({ conversation, host, deciding, onOpenDraft, autoOpen, initialRow }: DecisionsOptions) {
  const { i18n } = useTranslation();
  const [state, dispatch] = useReducer(exerciseDecisionReducer, initialRow, initialDecisionState);
  const anyWriting = state.writing.size > 0 || deciding.size > 0;
  const knownEquipment = (code: string) => i18n.exists(`exerciseLibrary.equipment.${code}`);

  const resultOf = (turnId: number): AiBuildResult | null => {
    const turn = conversation.turns.find((each) => each.id === turnId);
    return turn?.result?.kind === 'build' ? turn.result.result : null;
  };
  const viewOf = (turnId: number, exercises: readonly AiUnresolvedExercise[]): OutsideCatalogueView => {
    const reading = draftDecisionReading(state, turnId, exercises.map((exercise) => exercise.name));
    // A decision the conversation is still sending from elsewhere (a change of Buddy's own pick)
    // holds its row as one of this list's own writes does.
    const sending = exercises.filter((exercise) => deciding.has(decisionKey(turnId, exercise.name))).map((exercise) => [exercise.name, null] as const);
    return outsideCatalogueView({ exercises, ...reading, writing: new Map([...reading.writing, ...sending]), knownEquipment, anyWriting });
  };

  const armedResult = state.armed === null ? null : resultOf(state.armed);
  const due =
    state.armed !== null &&
    armedResult !== null &&
    autoOpen &&
    autoOpenDue({
      armed: true,
      view: viewOf(state.armed, armedResult.unresolved),
      valuesToDecide: armedResult.valuesToDecide.length,
      // Any write at all, the session panel's flush before a decision included: the draft never
      // opens over something still on its way.
      writing: anyWriting,
    });
  const dueTurn = due ? state.armed : null;

  const openDraft = (turnId: number) => {
    dispatch({ type: 'opened' });
    const result = resultOf(turnId);
    if (result !== null) onOpenDraft(turnId, result);
  };
  const openArmed = useEffectEvent(openDraft);
  // Owner requirement (board E): the list shows „Wszystkie ćwiczenia w katalogu" for a moment and
  // then the draft opens in the builder by itself. The delay is that moment, not a mask over
  // anything: a reopened row, a write, the trainer's own „Otwórz w builderze" or leaving cancels it.
  useEffect(() => {
    if (dueTurn === null) return;
    const timer = window.setTimeout(() => {
      openArmed(dueTurn);
    }, AUTO_OPEN_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [dueTurn]);

  /**
   * `arms`: whether a decision here can end in the draft opening by itself. The list's do; a change
   * of one of Buddy's own picks under „Założyłem" does not, since nothing outside the catalogue was
   * waiting on it.
   */
  const decisionsOf = (turnId: number, exercises: readonly AiUnresolvedExercise[], arms: boolean): DraftDecisions => {
    const key = (name: string) => decisionKey(turnId, name);
    const view = viewOf(turnId, exercises);
    const busy = (name: string) => state.writing.has(key(name)) || deciding.has(key(name));
    // The one path every decision takes: it leaves, and the rows change only once it has landed.
    const land = async (decisions: readonly AiNamedDecision[]): Promise<boolean> => {
      const names = decisions.map(({ name }) => name);
      const landed = await host.decide(turnId, decisions);
      dispatch(landed ? { type: 'landed', turnId, names, arms } : { type: 'refused', turnId, names });
      return landed;
    };
    const decide = (decisions: readonly AiNamedDecision[], route: DecisionRoute | null): Promise<boolean> => {
      if (decisions.some(({ name }) => busy(name))) return Promise.resolve(false);
      dispatch({ type: 'sent', turnId, decisions: decisions.map(({ name }) => ({ name, route })) });
      return land(decisions);
    };
    const { catalogue } = host;
    return {
      view,
      opening: dueTurn === turnId,
      busy,
      adding: (name) => state.writing.get(key(name))?.kind === 'quick',
      toggle: (name) => {
        dispatch({ type: 'toggle', turnId, name, isOpen: view.rows.some((row) => row.expanded && row.exercise.name === name) });
      },
      reopen: (name) => {
        dispatch({ type: 'reopen', turnId, name });
      },
      keep: (name) => {
        dispatch({ type: 'keep', turnId, name });
      },
      pick: (name, exerciseId, target) => decide([{ name, decision: { kind: 'use', exerciseId } }], { kind: 'pick', target }),
      skip: (name) => decide([{ name, decision: { kind: 'skip' } }], null),
      quickAdd:
        catalogue.kind === 'here'
          ? async (chosen) => {
              const names = chosen.map((exercise) => exercise.name);
              if (names.some(busy)) return false;
              dispatch({ type: 'sent', turnId, decisions: names.map((name) => ({ name, route: { kind: 'quick' } })) });
              const { added, failed } = await catalogue.quickAdd(turnId, chosen);
              if (failed.length > 0) {
                dispatch({ type: 'addFailed', turnId, failures: failed.map(({ name, status }) => ({ name, failure: addFailureOf(status) })) });
              }
              return added.length > 0 && (await land(added)) && failed.length === 0;
            }
          : null,
      verify:
        catalogue.kind === 'here'
          ? async (exercise, opener) => {
              // Nothing is recorded while the creator is open: only its save is a decision, and a
              // creator closed unsaved leaves the row, and the timer, exactly as they were.
              const exerciseId = await catalogue.verify(turnId, exercise, opener);
              if (exerciseId === null) return false;
              return decide([{ name: exercise.name, decision: { kind: 'use', exerciseId } }], { kind: 'verified' });
            }
          : null,
      handOver:
        catalogue.kind === 'page'
          ? (name) => {
              catalogue.open(turnId, name);
            }
          : null,
      open: () => {
        openDraft(turnId);
      },
    };
  };

  return {
    /** The list under a draft's program card. */
    forDraft: (turnId: number, exercises: readonly AiUnresolvedExercise[]) => decisionsOf(turnId, exercises, true),
    /** The cards of Buddy's own picks opened to change under „Założyłem". */
    forChanges: (turnId: number, exercises: readonly AiUnresolvedExercise[]) => decisionsOf(turnId, exercises, false),
  };
}
